import AddRounded from '@mui/icons-material/AddRounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable, type Column } from '../../../components/DataTable';
import { Money } from '../../../components/Money';
import { StatusChip } from '../../../components/StatusChip';
import { EmptyState, PageHeader } from '../../../components/states';
import { businessDate } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { ErrorState } from '../components/DocParts';
import { ListToolbar } from '../components/ListToolbar';
import { salesKeys, type SalesInvoiceRow } from '../lib/api';
import { dec } from '../lib/decimal';
import { invoiceStatus } from '../lib/documents';
import { apiFilters, useListParams } from '../lib/useListParams';
import { useExportSelection } from '../../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../../attachments';

const STATUSES = [
  { value: 'POSTED', label: 'Posted' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export function InvoicesPage() {
  const exportSelection = useExportSelection('sales_invoice');
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const [params, set] = useListParams();
  const status = STATUSES.some((s) => s.value === params.status)
    ? (params.status as 'POSTED' | 'CANCELLED')
    : undefined;

  const list = useQuery({
    queryKey: [...salesKeys.invoices, 'list', params],
    queryFn: () =>
      unwrap(
        api.GET('/sales-invoices', {
          params: { query: { ...apiFilters(params), ...(status ? { status } : {}) } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const filtered = !!(params.q || params.status || params.from || params.to);

  const fileCounts = useAttachmentCounts(
    'SALES_INVOICE',

    (list.data?.data ?? []).map((r) => r.id),
  );

  const columns: Column<SalesInvoiceRow>[] = [
    {
      key: 'number',
      header: 'Invoice',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {r.number}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
            {r.contact.name}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (r) => businessDate(r.docDate, format),
      hideBelow: 'md',
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (r) => r.contact.name,
      hideBelow: 'sm',
    },
    {
      key: 'due',
      header: 'Due',
      render: (r) => (
        <Box component="span" sx={{ color: r.overdue ? 'error.main' : undefined }}>
          {businessDate(r.dueDate, format)}
        </Box>
      ),
      hideBelow: 'lg',
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const s = invoiceStatus(r);
        return <StatusChip status={s.status} label={s.label} />;
      },
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (r) => <Money value={r.total} strong />,
    },
    {
      key: 'outstanding',
      header: 'Balance due',
      align: 'right',
      hideBelow: 'md',
      render: (r) =>
        r.status === 'CANCELLED' || dec(r.outstanding) === 0n ? (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ) : (
          <Money value={r.outstanding} />
        ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Invoices"
        subtitle="Every sale posted to a customer’s account, with what is still to be paid."
        actions={
          <Stack direction="row" spacing={1.25}>
            {can('sales.invoice') && (
              <Button
                variant="outlined"
                startIcon={<AddRounded />}
                onClick={() => void navigate('/sales/invoices/new')}
              >
                New invoice
              </Button>
            )}
            {can('sales.deliver', 'sales.invoice') && (
              <Button
                variant="contained"
                startIcon={<PointOfSaleRounded />}
                onClick={() => void navigate('/sales/counter')}
              >
                Fast sale
              </Button>
            )}
          </Stack>
        }
      />
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <DataTable
            {...exportSelection.table}
            columns={[...columns, ...filesColumn<SalesInvoiceRow>(fileCounts)]}
            rows={list.data?.data}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            page={list.data ? { ...list.data.page, page: params.page } : undefined}
            onPageChange={(page) => set({ page })}
            onPageSizeChange={(pageSize) => set({ pageSize })}
            onRowClick={(r) => void navigate(`/sales/invoices/${r.id}`)}
            toolbar={
              <ListToolbar
                params={params}
                set={set}
                statuses={STATUSES}
                placeholder="Search number or customer…"
              />
            }
            empty={
              filtered ? (
                <EmptyState
                  icon={<ReceiptLongRounded />}
                  title="No invoices match"
                  body="Try another search or clear the filters."
                  action={{
                    label: 'Clear filters',
                    onClick: () => set({ q: '', status: '', from: '', to: '' }),
                  }}
                />
              ) : (
                <EmptyState
                  icon={<ReceiptLongRounded />}
                  title="No invoices yet"
                  body="Ring up a sale at the counter or post an invoice for a customer — it shows up here."
                  action={
                    can('sales.deliver', 'sales.invoice')
                      ? { label: 'Start a sale', onClick: () => void navigate('/sales/counter') }
                      : undefined
                  }
                />
              )
            }
          />
          {exportSelection.dialog}
        </>
      )}
    </Box>
  );
}
